import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';

const NewRequest = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [showNewLocation, setShowNewLocation] = useState(false);
  const [locForm, setLocForm] = useState({
    label: 'Home',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
  });
  const [form, setForm] = useState({
    service_location_id: '',
    service_type: 'standard_clean',
    scheduled_start: '',
    notes: '',
  });

  const loadLocations = useCallback(() => {
    apiFetch('/locations').then((r) => {
      const list = r.data || [];
      setLocations(list);
      if (list.length === 1) {
        setForm((f) =>
          f.service_location_id ? f : { ...f, service_location_id: list[0].id }
        );
      }
    });
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const addLocation = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch('/locations', {
        method: 'POST',
        body: JSON.stringify(locForm),
      });
      toast.success('Location added');
      setShowNewLocation(false);
      setForm((f) => ({ ...f, service_location_id: res.data.id }));
      loadLocations();
    } catch (err) {
      toast.error(err.message || 'Failed to add location');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_location_id) {
      toast.error('Add a service location first');
      return;
    }
    try {
      const res = await apiFetch('/requests', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      toast.success('Request submitted');
      navigate(`/requests/${res.data.id}`);
    } catch (err) {
      toast.error(err.message || 'Failed to submit');
    }
  };

  return (
    <div className="max-w-lg">
      <Link to="/requests" className="text-sm text-teal-700 mb-4 inline-block">
        ← My requests
      </Link>
      <h1 className="text-2xl font-semibold mb-6">Request a cleaning</h1>

      {locations.length === 0 && !showNewLocation && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          Add your service address before submitting a request.
        </p>
      )}

      {!showNewLocation ? (
        <button
          type="button"
          onClick={() => setShowNewLocation(true)}
          className="text-sm text-teal-700 mb-4 hover:underline"
        >
          + Add address
        </button>
      ) : (
        <form onSubmit={addLocation} className="bg-white p-4 rounded-xl shadow mb-6 space-y-3">
          <h2 className="font-medium text-sm">New address</h2>
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="Label (e.g. Home)"
            value={locForm.label}
            onChange={(e) => setLocForm({ ...locForm, label: e.target.value })}
          />
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="Street address *"
            value={locForm.address_line1}
            onChange={(e) => setLocForm({ ...locForm, address_line1: e.target.value })}
            required
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              className="border rounded-lg px-3 py-2 text-sm"
              placeholder="City *"
              value={locForm.city}
              onChange={(e) => setLocForm({ ...locForm, city: e.target.value })}
              required
            />
            <input
              className="border rounded-lg px-3 py-2 text-sm"
              placeholder="State *"
              value={locForm.state}
              onChange={(e) => setLocForm({ ...locForm, state: e.target.value })}
              required
            />
          </div>
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="ZIP *"
            value={locForm.postal_code}
            onChange={(e) => setLocForm({ ...locForm, postal_code: e.target.value })}
            required
          />
          <div className="flex gap-2">
            <button type="submit" className="bg-teal-700 text-white px-3 py-2 rounded-lg text-sm">
              Save address
            </button>
            <button
              type="button"
              onClick={() => setShowNewLocation(false)}
              className="text-sm text-slate-600"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <form onSubmit={submit} className="bg-white p-6 rounded-xl shadow space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Location *</label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={form.service_location_id}
            onChange={(e) => setForm({ ...form, service_location_id: e.target.value })}
            required
          >
            <option value="">Select address</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label || l.address_line1} — {l.city}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Service</label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={form.service_type}
            onChange={(e) => setForm({ ...form, service_type: e.target.value })}
          >
            <option value="standard_clean">Standard clean</option>
            <option value="deep_clean">Deep clean</option>
            <option value="move_out">Move-out clean</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Preferred date & time</label>
          <input
            type="datetime-local"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={form.scheduled_start}
            onChange={(e) => setForm({ ...form, scheduled_start: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Notes</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 text-sm"
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        <button type="submit" className="w-full bg-teal-700 text-white py-2.5 rounded-lg text-sm">
          Submit request
        </button>
      </form>
    </div>
  );
};

export default NewRequest;
